import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/lib/logger';

export type AdminRole = 'superadmin' | 'support' | 'readonly' | null;

export type AdminSection = 'dashboard' | 'providers' | 'ai-agents' | 'payments' | 'support' | 'team' | 'plans' | 'features';

const ROLE_PERMISSIONS: Record<Exclude<AdminRole, null>, AdminSection[]> = {
  superadmin: ['dashboard', 'providers', 'ai-agents', 'payments', 'support', 'team', 'plans', 'features'],
  support: ['support'],
  readonly: ['dashboard', 'providers'],
};

interface UseAdminRoleReturn {
  role: AdminRole;
  isLoading: boolean;
  isSuperadmin: boolean;
  isSupport: boolean;
  isReadonly: boolean;
  hasAdminAccess: boolean;
  canAccess: (section: AdminSection) => boolean;
  allowedSections: AdminSection[];
}

/**
 * Hook para gestionar roles y permisos de administrador.
 *
 * Roles disponibles:
 * - superadmin: Acceso total a todas las secciones
 * - support: Solo acceso a la sección de soporte
 * - readonly: Acceso a dashboard y proveedores sin datos sensibles
 *
 * @example
 * const { role, canAccess, isSuperadmin } = useAdminRole();
 *
 * if (!canAccess('payments')) {
 *   return <AccessDenied />;
 * }
 */
export function useAdminRole(): UseAdminRoleReturn {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-role', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        logger.info('[useAdminRole] No user ID');
        return null;
      }

      logger.info('[useAdminRole] Fetching role for user:', user.id);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('admin_role')
        .eq('id', user.id)
        .single();

      if (error) {
        logger.error('[useAdminRole] Error fetching admin role:', error);
        return null;
      }

      const adminRole = profile?.admin_role as AdminRole;

      // Validar que sea un rol válido
      if (adminRole && ['superadmin', 'support', 'readonly'].includes(adminRole)) {
        logger.info('[useAdminRole] Valid role found:', adminRole);
        return adminRole;
      }

      logger.info('[useAdminRole] No valid admin role');
      return null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const role: AdminRole = data ?? null;

  const isSuperadmin = role === 'superadmin';
  const isSupport = role === 'support';
  const isReadonly = role === 'readonly';
  const hasAdminAccess = role !== null;

  // Debug logs
  logger.info('[useAdminRole] Current state:', {
    role,
    isLoading,
    hasAdminAccess,
    isSuperadmin,
    isSupport,
    isReadonly,
  });

  const canAccess = (section: AdminSection): boolean => {
    if (!role) return false;
    return ROLE_PERMISSIONS[role]?.includes(section) ?? false;
  };

  const allowedSections: AdminSection[] = role ? ROLE_PERMISSIONS[role] : [];

  return {
    role,
    isLoading,
    isSuperadmin,
    isSupport,
    isReadonly,
    hasAdminAccess,
    canAccess,
    allowedSections,
  };
}

/**
 * Mapeo de rutas a secciones para verificación de acceso.
 */
export const ROUTE_TO_SECTION: Record<string, AdminSection> = {
  '/admin': 'dashboard',
  '/admin/providers': 'providers',
  '/admin/ai-agents': 'ai-agents',
  '/admin/payments': 'payments',
  '/admin/support': 'support',
  '/admin/team': 'team',
  '/admin/plans': 'plans',
  '/admin/features': 'features',
};

/**
 * Obtiene la primera sección permitida para un rol.
 */
export function getDefaultSectionForRole(role: AdminRole): string {
  if (!role) return '/';

  const sections = ROLE_PERMISSIONS[role];
  if (!sections || sections.length === 0) return '/';

  const sectionRoutes: Record<AdminSection, string> = {
    dashboard: '/admin',
    providers: '/admin/providers',
    'ai-agents': '/admin/ai-agents',
    payments: '/admin/payments',
    support: '/admin/support',
    team: '/admin/team',
    plans: '/admin/plans',
    features: '/admin/features',
  };

  return sectionRoutes[sections[0]] || '/admin';
}
